import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Search, UserCheck, Loader2, User } from "lucide-react";

export default function AssignCustomerDialog({ open, onOpenChange, requestId, onLinked }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkingId, setLinkingId] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    setSearching(true);
    try {
      const { data } = await api.get(`/customers`, { params: { query } });
      setResults(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha na busca");
    } finally {
      setSearching(false);
    }
  };

  const link = async (customerId) => {
    setLinkingId(customerId);
    try {
      await api.patch(`/requests/${requestId}/assign-customer`, { customer_id: customerId });
      toast.success("Chamado associado ao cliente!");
      onOpenChange(false);
      onLinked?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLinkingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assign-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserCheck className="h-5 w-5 text-[#1e7be0]" /> Associar a cliente cadastrado</DialogTitle>
          <DialogDescription>Busque por nome, e-mail, telefone ou CPF/CNPJ.</DialogDescription>
        </DialogHeader>
        <form onSubmit={search} className="flex gap-2">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar cliente..." data-testid="assign-customer-search-input" />
          <Button type="submit" className="gap-1.5 bg-blue-600 hover:bg-blue-700" disabled={searching} data-testid="assign-customer-search-button">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </Button>
        </form>
        <div className="max-h-72 overflow-y-auto space-y-2" data-testid="assign-customer-results">
          {results.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Nenhum cliente encontrado. Faça uma busca.</p>
          ) : results.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-lg border border-slate-200 p-2.5" data-testid={`assign-customer-${c.id}`}>
              <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-slate-400" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-800 truncate">{c.name}</p>
                <p className="text-xs text-slate-500 truncate">{c.email} · {c.phone || "-"}</p>
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 border-[#1e7be0] text-[#1e7be0] hover:bg-blue-50 flex-shrink-0"
                onClick={() => link(c.id)} disabled={linkingId === c.id} data-testid={`assign-customer-select-${c.id}`}>
                {linkingId === c.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Associar"}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
